import React, { useContext, useState, memo } from 'react';
import { Box, Typography, Paper, Chip, Link, Button, CircularProgress } from '@mui/material';
import TablePagination from "./ManagedDropdown";
import ViewListIcon from '@mui/icons-material/ViewList';
import AuthContext from "./auth/Context"; 
import SecureFileDisplay from './SecureFileDisplay';
import Moment from 'moment';
import { numeral } from '../utils';
import { formatFieldValue } from './fieldFormat';
import ViewValue, { summarizeValue } from './ViewValue';
import { viewValueShape } from './viewTabsRules';

// Import 2 component đã tách
import TableListView from './DataTableListView';
import CardListView from './DataCardListView';
import CompactListView from './DataCompactListView';
import DynamicCustomList from "./DynamicCustomList";


// ============================================================================
// HÀM FORMAT DỮ LIỆU HIỂN THỊ ĐA NĂNG
// ============================================================================
export const formatDisplayValue = (value, fieldDef, item, context, isViewMode = false) => {
  if (value === undefined || value === null || value === '') {
     return isViewMode ? <Typography variant="body1" color="text.secondary">---</Typography> : '---';
  }

  const fieldName = String(fieldDef.field).toLowerCase();

  if (fieldDef.type === 'HTML' || fieldDef.type === 'RichText') {
    return (
      <Box sx={{ mt: isViewMode ? 1 : 0, p: isViewMode ? 2 : 0, bgcolor: isViewMode ? 'background.default' : 'transparent', borderRadius: 2, border: isViewMode ? '1px solid' : 'none', borderColor: 'divider', '& img': { maxWidth: '100%', height: 'auto', borderRadius: '8px' }, '& a': { color: 'primary.main' }, ...( !isViewMode && { display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' } ) }} dangerouslySetInnerHTML={{ __html: value }} />
    );
  }

  if (fieldDef.type === 'File' || fieldDef.type === 'FileArray') {
    return <SecureFileDisplay value={value} context={context} defaultExpanded={isViewMode} />;
  }

  // GIÁ TRỊ PHỨC (object / mảng object / mảng giá trị). Trước đây rơi xuống `String(value)` ở cuối hàm
  // và in ra "[object Object]". Chọn cách vẽ theo DỮ LIỆU THẬT — cấu hình dịch từ listinfo hay khai
  // `String`/`Mixed` cho field thực chất chứa object. Ô bảng/thẻ chỉ tóm tắt một dòng; trang xem vẽ đủ
  // (bảng cho mảng object, cặp nhãn/giá trị cho object) — xem ViewValue.jsx.
  if (typeof value === 'object' && !(value instanceof Date) && viewValueShape(value, fieldDef) !== 'scalar') {
    if (!isViewMode) return summarizeValue(value);
    return <ViewValue value={value} field={fieldDef} row={item} formatCell={(v, f, r) => formatDisplayValue(v, f, r, context, false)} />;
  }

  const linkSx = { fontWeight: 'bold', color: 'primary.main', fontSize: isViewMode ? '1rem' : '0.875rem', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } };
  
  if (fieldName.includes('dien_thoai') || fieldName.includes('phone') || fieldName.includes('sdt')) return <Link href={`tel:${value}`} onClick={e => e.stopPropagation()} sx={linkSx}>{value}</Link>;
  if (fieldName.includes('email')) return <Link href={`mailto:${value}`} onClick={e => e.stopPropagation()} sx={{...linkSx, fontWeight: 'normal'}}>{value}</Link>;
  if (fieldName.includes('url') || fieldName.includes('link') || fieldName.includes('website')) {
    const href = String(value).startsWith('http') ? value : `https://${value}`;
    return <Link href={href} target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()} sx={{...linkSx, fontWeight: 'normal', wordBreak: 'break-all'}}>{value}</Link>;
  }

  const wrapText = (text) => isViewMode ? <Typography variant="body1" fontWeight="500" color="text.primary" sx={{ wordBreak: 'break-word', whiteSpace: 'pre-line' }}>{text}</Typography> : text;

  // Cột có khai `format` (mẫu numeral cho số / moment cho ngày) thì theo khai báo — bù đúng khoảng trống
  // so với listinfo. Dùng numeral BẢN RÚT GỌN của utils (định dạng theo vi-VN) để cột có format và cột
  // không format hiển thị cùng một kiểu phân cách. Không khai format -> trả undefined -> giữ nguyên như cũ.
  const formattedByConfig = formatFieldValue(value, fieldDef, { numeral, moment: Moment });
  if (formattedByConfig !== undefined) return wrapText(formattedByConfig);

  switch (fieldDef.type) {
    case 'Number': return wrapText(new Intl.NumberFormat('vi-VN').format(value));
    case 'Date': 
    case 'DateTime':
      const dateOpt = fieldDef.type === 'Date' ? { day: '2-digit', month: '2-digit', year: 'numeric' } : { hour: '2-digit', minute: '2-digit', day: '2-digit', month: '2-digit', year: 'numeric' };
      return wrapText(new Date(value).toLocaleDateString('vi-VN', dateOpt));
    case 'Boolean': return <Chip label={value ? 'Có' : 'Không'} size="small" sx={{ height: 20, fontSize: '0.65rem', fontWeight: 'bold', bgcolor: value ? 'success.light' : 'error.light', color: value ? 'success.dark' : 'error.dark' }}/>;
    case 'Select':
    case 'Autocomplete':
      const isMultiple = Array.isArray(value);
      let labelResult = value;
      if (fieldDef.apiMapping && item) {
          const apiLabelMap = fieldDef.apiMapping.label || 'name';
          const formLabelMap = fieldDef.apiMapping.label_as || apiLabelMap;
          const labelData = item[formLabelMap];
          if (labelData) return wrapText(Array.isArray(labelData) ? labelData.join(', ') : String(labelData)); 
      }
      if (isMultiple) {
          const selectedOpts = fieldDef.options?.filter(opt => value.includes(opt.value)) || [];
          labelResult = selectedOpts.length > 0 ? selectedOpts.map(o => o.label).join(', ') : value.join(', ');
      } else {
          const option = fieldDef.options?.find(opt => String(opt.value) === String(value));
          labelResult = option ? option.label : value;
      }
      return wrapText(labelResult);
    default: return wrapText(String(value));
  }
};

// ============================================================================
// COMPONENT CHÍNH (ĐÃ BỌC REACT.MEMO)
// ============================================================================
const DataListItems = memo(({
  data = [], 
  totalRecords = 0,
  fields = [], canEdit = false, canDelete = false, canCopy = false,
  gridProps = { xs: 12, sm: 6, md: 4, lg: 3 }, 
  viewMode = 'card', 
  
  loadingMore, hasMore, onLoadMore, 
  
  isSelectionMode = false,
  selectedIds = [],           
  onSelectionChange,          
  currentLimit = 20,
  onLimitChange,

  customListScript,

  activeRowId, onRowClick,   // dòng đang xem ở khung chi tiết (chỉ dùng ở chế độ bảng)

  // SỬA THẲNG TRÊN LƯỚI (chỉ ở chế độ bảng) — xem inlineEdit.js
  isEditableCell, dirtyRowIds, savingRowId, onCellChange, onCommitRow, onCancelRow,

  onEdit, onDelete, onView, onCopy, renderItem,
  renderRowActions,   // (item, 'list') => nút tuỳ chỉnh của dòng ở cột Thao tác — xem DataRowActions.jsx
}) => {
  const context = useContext(AuthContext); 

  // STATE PHÂN TRANG LOCAL CHO TABLE
  const [page, setPage] = useState(0);

  if (!data || data.length === 0) return (<Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 8, opacity: 0.6 }}><ViewListIcon sx={{ fontSize: 60, color: '#8e8e93', mb: 2 }} /><Typography variant="body1" color="text.secondary" fontWeight="500">Chưa có dữ liệu nào</Typography></Box>);

  // Phân trang dữ liệu
  const paginatedData = viewMode === 'table' ? data.slice(page * currentLimit, page * currentLimit + currentLimit) : data;
  const displayTotal = totalRecords > data.length ? totalRecords : data.length;

  const handleChangePage = (event, newPage) => {
    if(loadingMore) return;
    setPage(newPage);
    const requiredDataLength = (newPage + 1) * currentLimit;
    if (data.length < requiredDataLength && hasMore) {
        onLoadMore();
    }
  };

  const handleChangeRowsPerPage = (event) => {
    const newLimit = parseInt(event.target.value, 10);
    if (onLimitChange) onLimitChange(newLimit); 
    setPage(0); 
  };

  // ================= BỘ LỌC AN TOÀN CHỐNG SCRIPT RỖNG =================
  const compactListScript = customListScript ? customListScript.replace(/\s+/g, '') : '';
  const isFakeListScript = compactListScript === '' || compactListScript.includes('<MUI.Card></MUI.Card>');
  const shouldUseCustomList = customListScript && !isFakeListScript;

  return (
    <Paper elevation={0} sx={{ 
        display: 'flex', flexDirection: 'column', 
        flexGrow: 1, 
        height: viewMode === 'table' ? '100%' : 'auto', 
        border: viewMode === 'table' ? '1px solid #e0e0e0' : 'none', 
        borderRadius: 2, bgcolor: 'transparent',
        overflow: viewMode === 'table' ? 'hidden' : 'visible',
    }}>
      
      {/* ================= RẼ NHÁNH HIỂN THỊ ================= */}
      {shouldUseCustomList ? (
         // 1. RENDER BẰNG CODE CỦA USER
         <Box sx={{ flexGrow: 1, overflowX: 'hidden' }}>
             <DynamicCustomList 
                script={customListScript} 
                dataList={viewMode === 'table' ? paginatedData : data} 
                handleEdit={onEdit} 
                handleDelete={onDelete}
                handleCopy={onCopy}
                handleView={onView}
                context={context}
             />
         </Box>
      ) : viewMode === 'table' ? (
         // 2. RENDER TABLE MẶC ĐỊNH
         <TableListView 
            data={paginatedData} isLoadingMore={loadingMore && paginatedData.length === 0}
            fields={fields} selectedIds={selectedIds} onSelectionChange={onSelectionChange} 
            onView={onView} onEdit={onEdit} onCopy={onCopy} onDelete={onDelete}
            canEdit={canEdit} canDelete={canDelete} canCopy={canCopy} context={context}
            renderRowActions={renderRowActions}
            pageIndex={page} rowsPerPage={currentLimit}
            activeRowId={activeRowId} onRowClick={onRowClick}
            isEditableCell={isEditableCell} dirtyRowIds={dirtyRowIds} savingRowId={savingRowId}
            onCellChange={onCellChange} onCommitRow={onCommitRow} onCancelRow={onCancelRow}
         />
      ): viewMode === 'compact' ? (
         <CompactListView 
               data={data} 
               fields={fields} isSelectionMode={isSelectionMode} selectedIds={selectedIds} 
               onSelectionChange={onSelectionChange} onView={onView} onEdit={onEdit} onCopy={onCopy} onDelete={onDelete} 
               canEdit={canEdit} canDelete={canDelete}  canCopy={canCopy} context={context} gridProps={gridProps} renderItem={renderItem}
               renderRowActions={renderRowActions}
            />
      ) : (
         // 3. RENDER CARD LƯỚI MẶC ĐỊNH
         <Box sx={{ flexGrow: 1, p: 0.5 }}>
            <CardListView 
               data={data} 
               fields={fields} isSelectionMode={isSelectionMode} selectedIds={selectedIds} 
               onSelectionChange={onSelectionChange} onView={onView} onEdit={onEdit} onCopy={onCopy} onDelete={onDelete} 
               canEdit={canEdit} canDelete={canDelete}  canCopy={canCopy} context={context} gridProps={gridProps} renderItem={renderItem}
               renderRowActions={renderRowActions}
            />
         </Box>
      )}
      
      {/* ================= THANH ĐIỀU HƯỚNG BÊN DƯỚI ================= */}
      {viewMode === 'table' && (
        <Box sx={{ flexShrink: 0, borderTop: '1px solid #e0e0e0', bgcolor: '#f8f9fa' }}>
            <TablePagination type="tablepagination"
              component="div" count={displayTotal} page={page} onPageChange={handleChangePage}
              rowsPerPage={currentLimit} onRowsPerPageChange={handleChangeRowsPerPage}
              labelRowsPerPage="Số dòng:" labelDisplayedRows={({ from, to, count }) => `${from} - ${to} trong tổng ${count}`}
              rowsPerPageOptions={[10, 20, 50, 100]}
              sx={{ '.MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows': { mt: 2 } }}
            />
        </Box>
      )}

      {viewMode === 'card' && (
          <Box sx={{ mt: 2 }}>
            {hasMore && data.length > 0 && (<Button fullWidth variant="text" onClick={onLoadMore} disabled={loadingMore} sx={{ py: 1.5, color: "primary.main", fontWeight: 'bold', textTransform: 'none', fontSize: '0.95rem', bgcolor: 'white', borderRadius: 3, border: '1px solid #e5e5ea' }}>{loadingMore ? <CircularProgress size={20} sx={{ color: "primary.main" }} /> : 'Tải thêm dữ liệu...'}</Button>)}
          </Box>
      )}
    </Paper>
  );
}, (prevProps, nextProps) => {
  // Cập nhật chặn re-render: Bao gồm cả customListScript
  return (
    prevProps.data === nextProps.data &&
    prevProps.selectedIds === nextProps.selectedIds &&
    prevProps.viewMode === nextProps.viewMode &&
    prevProps.isSelectionMode === nextProps.isSelectionMode &&
    prevProps.loadingMore === nextProps.loadingMore &&
    prevProps.hasMore === nextProps.hasMore &&
    prevProps.currentLimit === nextProps.currentLimit &&
    prevProps.totalRecords === nextProps.totalRecords &&
    prevProps.fields === nextProps.fields &&
    prevProps.customListScript === nextProps.customListScript &&
    // Thiếu dòng này thì bấm sang dòng khác sẽ không đổi được vệt sáng.
    prevProps.activeRowId === nextProps.activeRowId &&
    // onRowClick lật giữa undefined <-> hàm khi đổi giao diện hoặc khi cấu hình có/không có script
    // handle_cell_clicked. Thiếu dòng này thì bảng giữ nguyên trạng thái bấm được/không bấm được cũ.
    prevProps.onRowClick === nextProps.onRowClick &&
    // Sửa trên lưới: thiếu ba dòng này thì ô nhập/vệt vàng/vòng quay lưu đều không cập nhật, câm lặng.
    prevProps.isEditableCell === nextProps.isEditableCell &&
    prevProps.dirtyRowIds === nextProps.dirtyRowIds &&
    prevProps.savingRowId === nextProps.savingRowId
  );
});

export default DataListItems;